import { Plugin, PluginKey } from '@tiptap/pm/state'
/**
 * Sticky stored marks — makes toolbar-toggled marks survive continuous
 * typing under `inclusive: false`.
 *
 * ProseMirror consumes `storedMarks` on the FIRST inserted character; for
 * an inclusive mark the continuation then rides `$from.marks()` at the
 * caret, but a NON-inclusive mark (our `code`, per mark-input-rules.ts)
 * excludes itself at its own trailing edge — so a toolbar Code toggle
 * marked exactly one character and silently dropped the rest (found by
 * the UI review pass).
 *
 * Two plugins: (1) re-arm just-consumed stored marks after every typing
 * transaction: toggle Code → type freely in code → toggle off (or move
 * the caret / click elsewhere, which clears stored marks) → plain. The
 * backtick input rule is unaffected: it finishes with `removeStoredMark`,
 * leaving an EMPTY stored-mark set, and empty sets are deliberately not
 * re-armed — so post-rule typing stays plain (the Slack escape).
 * (2) keyboard re-entry: arrowing back to a code span's trailing edge
 * arms its marks so typing continues in code (staging finding — one
 * ArrowLeft into a finished pill typed plain with no way to append).
 */
import { Extension } from '@tiptap/react'

export const StickyStoredMarks = Extension.create({
  name: 'stickyStoredMarks',
  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('stickyStoredMarks'),
        appendTransaction(transactions, oldState, newState) {
          // Only when marks were armed, got consumed, by a doc-changing
          // (typing-shaped) transaction, and the caret is a plain cursor.
          if (!oldState.storedMarks?.length) return null
          if (newState.storedMarks) return null
          if (!newState.selection.empty) return null
          if (!transactions.some((tr) => tr.docChanged)) return null
          return newState.tr.setStoredMarks(oldState.storedMarks)
        },
      }),
      // Keyboard re-entry at a non-inclusive mark's trailing edge. Fresh
      // completions are unaffected (no selection move happened), so the
      // post-completion plain escape stays intact; mouse selection is
      // excluded (`pointer` meta) so a click at the edge keeps
      // ProseMirror's side-neutral default.
      new Plugin({
        key: new PluginKey('nonInclusiveMarkReentry'),
        appendTransaction(transactions, oldState, newState) {
          const moved = transactions.some(
            (tr) => tr.selectionSet && !tr.docChanged && !tr.getMeta('pointer'),
          )
          if (!moved || newState.storedMarks) return null
          const { $head, empty } = newState.selection
          if (!empty || !oldState.selection.empty) return null
          if (Math.abs($head.pos - oldState.selection.$head.pos) !== 1) return null
          const before = $head.nodeBefore
          if (!before?.isText) return null
          const reentrant = before.marks.filter(
            (mark) =>
              mark.type.spec.inclusive === false && !$head.nodeAfter?.marks.some((m) => m.eq(mark)),
          )
          if (reentrant.length === 0) return null
          return newState.tr.setStoredMarks(before.marks)
        },
      }),
    ]
  },
})
